import { useState } from "react";
import { useParams, Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getBuckets,
  listLedger,
  postManualLedger,
  type AllowanceBucket,
  type AllowanceLedgerEntry,
  type ManualLedgerInput,
} from "../lib/api-modules/allowance.js";
import { Card, Pill, SectionHeader } from "../components/design-system/index.js";
import { formatCentsCompact } from "../lib/util/money.js";
import { Toast, useToast } from "../components/toast.js";

/**
 * Phase 13 — `/family/<member_id>/buckets` page.
 *
 * Three big bucket cards (Spend / Save / Give) showing running totals,
 * recent ledger entries, and a manual-credit / debit / transfer form.
 *
 * Bucket totals are computed server-side; UI just renders.
 */

export function FamilyMemberBucketsPage() {
  const { memberId } = useParams<{ memberId: string }>();
  const toast = useToast();
  const queryClient = useQueryClient();

  const bucketsQ = useQuery({
    queryKey: ["allowance", "buckets", memberId],
    queryFn: () => getBuckets(memberId!),
    enabled: !!memberId,
  });
  const ledgerQ = useQuery({
    queryKey: ["allowance", "ledger", memberId],
    queryFn: () => listLedger(memberId!),
    enabled: !!memberId,
  });

  const [showManual, setShowManual] = useState(false);

  if (!memberId) return <div className="p-6">Missing member_id</div>;

  return (
    <div className="mx-auto max-w-4xl p-6 lg:p-8">
      <SectionHeader
        title={
          <>
            Buckets · {bucketsQ.data?.member_name ?? "…"}
          </>
        }
        sub="Spend / Save / Give totals — running balances from chores + manual entries."
        action={
          <Link
            to={`/family/${memberId}/allowance`}
            className="rounded-md border border-[var(--color-border-default)] px-3 py-1.5 text-sm hover:bg-[var(--color-elevated)]"
          >
            ← Allowance
          </Link>
        }
      />

      {bucketsQ.isLoading && <p className="mt-6 text-sm">Loading…</p>}
      {bucketsQ.data && (
        <>
          <div className="mt-3 mb-6 grid grid-cols-3 gap-3">
            <BucketCard
              label="Spend"
              cents={bucketsQ.data.buckets.spend_cents}
              tone="accent"
            />
            <BucketCard
              label="Save"
              cents={bucketsQ.data.buckets.save_cents}
              tone="success"
            />
            <BucketCard
              label="Give"
              cents={bucketsQ.data.buckets.give_cents}
              tone="warning"
            />
          </div>
          <p className="mb-6 text-xs text-[var(--color-muted)]">
            Lifetime earned:{" "}
            <strong className="text-[var(--color-text)]">
              {formatCentsCompact(bucketsQ.data.lifetime.earned_cents)}
            </strong>
            {" · "}
            Lifetime spent:{" "}
            <strong className="text-[var(--color-text)]">
              {formatCentsCompact(bucketsQ.data.lifetime.spent_cents)}
            </strong>
            {" · "}
            {bucketsQ.data.ledger_entry_count} ledger entries
          </p>
        </>
      )}

      {!showManual && (
        <button
          type="button"
          onClick={() => setShowManual(true)}
          className="mb-4 rounded-md border border-[var(--color-border-default)] px-3 py-1.5 text-sm hover:bg-[var(--color-elevated)]"
        >
          + Manual credit / debit / transfer
        </button>
      )}
      {showManual && (
        <ManualLedgerForm
          memberId={memberId}
          onClose={() => setShowManual(false)}
          onSuccess={async () => {
            setShowManual(false);
            await Promise.all([
              queryClient.invalidateQueries({
                queryKey: ["allowance", "buckets", memberId],
              }),
              queryClient.invalidateQueries({
                queryKey: ["allowance", "ledger", memberId],
              }),
            ]);
            toast.show("Saved.");
          }}
        />
      )}

      <Card className="mb-6">
        <h3 className="mb-3 font-display text-lg font-medium">Recent ledger</h3>
        {ledgerQ.isLoading && <p className="text-sm">Loading…</p>}
        {ledgerQ.data && ledgerQ.data.entries.length === 0 && (
          <p className="text-sm text-[var(--color-muted)]">
            No entries yet — complete a chore or add a manual credit to get started.
          </p>
        )}
        {ledgerQ.data && ledgerQ.data.entries.length > 0 && (
          <ul className="divide-y divide-[var(--color-border-default)]">
            {ledgerQ.data.entries.slice(0, 30).map((e) => (
              <LedgerRow key={e.entry_id} entry={e} />
            ))}
          </ul>
        )}
      </Card>

      <Toast message={toast.message} />
    </div>
  );
}

function BucketCard({
  label,
  cents,
  tone,
}: {
  label: string;
  cents: number;
  tone: "accent" | "success" | "warning";
}) {
  return (
    <Card>
      <p className="text-xs uppercase tracking-wide text-[var(--color-muted)]">
        {label}
      </p>
      <p className="mt-2 font-display text-2xl font-medium text-[var(--color-text)]">
        {formatCentsCompact(cents)}
      </p>
      <Pill tone={tone}>{label}</Pill>
    </Card>
  );
}

function LedgerRow({ entry }: { entry: AllowanceLedgerEntry }) {
  const date = entry.timestamp.slice(0, 10);
  let line = "";
  let signed = "+";
  switch (entry.entry_type) {
    case "chore_completion":
      line = `Chore completed (${formatCentsCompact(entry.value_cents)} → ${formatCentsCompact(entry.bucket_allocation.spend_cents)} spend / ${formatCentsCompact(entry.bucket_allocation.save_cents)} save / ${formatCentsCompact(entry.bucket_allocation.give_cents)} give)`;
      signed = "+";
      break;
    case "manual_credit":
      line = `Manual credit (${formatCentsCompact(entry.value_cents)})`;
      signed = "+";
      break;
    case "manual_debit":
      line = `Spent ${formatCentsCompact(entry.value_cents)} from ${entry.bucket}`;
      signed = "−";
      break;
    case "bucket_transfer":
      line = `Transferred ${formatCentsCompact(entry.value_cents)} (${entry.from_bucket} → ${entry.to_bucket})`;
      signed = "·";
      break;
  }
  return (
    <li className="flex items-baseline justify-between gap-3 py-2 text-sm">
      <span className="min-w-0 flex-1">
        <span className="mr-2 text-[var(--color-muted)]">{date}</span>
        {line}
        {"note" in entry && entry.note && (
          <span className="block text-xs italic text-[var(--color-muted)]">
            “{entry.note}”
          </span>
        )}
      </span>
      <span className="font-mono text-xs text-[var(--color-muted)]">
        {signed}
        {formatCentsCompact(entry.value_cents)}
      </span>
    </li>
  );
}

function ManualLedgerForm({
  memberId,
  onClose,
  onSuccess,
}: {
  memberId: string;
  onClose: () => void;
  onSuccess: () => Promise<void>;
}) {
  const [entryType, setEntryType] = useState<ManualLedgerInput["entry_type"]>(
    "manual_credit",
  );
  const [valueDollars, setValueDollars] = useState("");
  const [bucket, setBucket] = useState<AllowanceBucket>("spend");
  const [fromBucket, setFromBucket] = useState<AllowanceBucket>("save");
  const [toBucket, setToBucket] = useState<AllowanceBucket>("give");
  const [creditSpend, setCreditSpend] = useState("");
  const [creditSave, setCreditSave] = useState("");
  const [creditGive, setCreditGive] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async (): Promise<void> => {
      const valueCents = Math.round(Number(valueDollars) * 100);
      if (!Number.isFinite(valueCents) || valueCents <= 0) {
        throw new Error("Value must be a positive number");
      }
      let payload: ManualLedgerInput;
      if (entryType === "manual_credit") {
        const s = Math.round(Number(creditSpend) * 100) || 0;
        const sv = Math.round(Number(creditSave) * 100) || 0;
        const g = Math.round(Number(creditGive) * 100) || 0;
        if (s + sv + g !== valueCents) {
          throw new Error(
            "Spend / Save / Give amounts must sum to the total value",
          );
        }
        payload = {
          entry_type: "manual_credit",
          value_cents: valueCents,
          bucket_allocation: {
            spend_cents: s,
            save_cents: sv,
            give_cents: g,
          },
          ...(note ? { note } : {}),
        };
      } else if (entryType === "manual_debit") {
        payload = {
          entry_type: "manual_debit",
          value_cents: valueCents,
          bucket,
          ...(note ? { note } : {}),
        };
      } else {
        if (fromBucket === toBucket) {
          throw new Error("From and To buckets must differ");
        }
        payload = {
          entry_type: "bucket_transfer",
          value_cents: valueCents,
          from_bucket: fromBucket,
          to_bucket: toBucket,
          ...(note ? { note } : {}),
        };
      }
      await postManualLedger(memberId, payload);
    },
    onSuccess: async () => {
      await onSuccess();
    },
    onError: (err) => setError(err instanceof Error ? err.message : String(err)),
  });

  return (
    <Card className="mb-6">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-display text-base font-medium">Manual ledger</h3>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-[var(--color-muted)] hover:underline"
        >
          Cancel
        </button>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          mut.mutate();
        }}
        className="grid grid-cols-1 gap-3 sm:grid-cols-2"
      >
        <div>
          <label className="mb-1 block text-xs font-medium">Entry type</label>
          <select
            value={entryType}
            onChange={(e) =>
              setEntryType(e.target.value as ManualLedgerInput["entry_type"])
            }
            className={inputClass}
          >
            <option value="manual_credit">Credit (give the kid money)</option>
            <option value="manual_debit">Debit (kid spent / donated)</option>
            <option value="bucket_transfer">Transfer between buckets</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">Value (USD)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            required
            value={valueDollars}
            onChange={(e) => setValueDollars(e.target.value)}
            className={inputClass}
          />
        </div>
        {entryType === "manual_credit" && (
          <>
            <div>
              <label className="mb-1 block text-xs font-medium">
                Spend amount
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={creditSpend}
                onChange={(e) => setCreditSpend(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium">
                Save amount
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={creditSave}
                onChange={(e) => setCreditSave(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium">
                Give amount
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={creditGive}
                onChange={(e) => setCreditGive(e.target.value)}
                className={inputClass}
              />
            </div>
          </>
        )}
        {entryType === "manual_debit" && (
          <div>
            <label className="mb-1 block text-xs font-medium">Bucket</label>
            <select
              value={bucket}
              onChange={(e) => setBucket(e.target.value as AllowanceBucket)}
              className={inputClass}
            >
              <option value="spend">Spend</option>
              <option value="save">Save</option>
              <option value="give">Give</option>
            </select>
          </div>
        )}
        {entryType === "bucket_transfer" && (
          <>
            <div>
              <label className="mb-1 block text-xs font-medium">From</label>
              <select
                value={fromBucket}
                onChange={(e) =>
                  setFromBucket(e.target.value as AllowanceBucket)
                }
                className={inputClass}
              >
                <option value="spend">Spend</option>
                <option value="save">Save</option>
                <option value="give">Give</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium">To</label>
              <select
                value={toBucket}
                onChange={(e) =>
                  setToBucket(e.target.value as AllowanceBucket)
                }
                className={inputClass}
              >
                <option value="spend">Spend</option>
                <option value="save">Save</option>
                <option value="give">Give</option>
              </select>
            </div>
          </>
        )}
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs font-medium">
            Note <span className="text-[var(--color-muted)]">(optional)</span>
          </label>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className={inputClass}
            maxLength={2000}
          />
        </div>
        {error && (
          <p className="sm:col-span-2 text-xs text-[var(--color-danger)]">
            {error}
          </p>
        )}
        <div className="sm:col-span-2 flex gap-2">
          <button
            type="submit"
            disabled={mut.isPending}
            className="rounded-md bg-[var(--color-accent)] px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60"
          >
            {mut.isPending ? "Saving…" : "Save entry"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-[var(--color-border-default)] px-3 py-1.5 text-sm"
          >
            Cancel
          </button>
        </div>
      </form>
    </Card>
  );
}

const inputClass =
  "w-full rounded-md border border-[var(--color-border-default)] bg-[var(--color-card)] px-2 py-1.5 text-sm text-[var(--color-text)] focus:border-[var(--color-accent)] focus:outline-none focus:ring-1 focus:ring-[var(--color-accent)]";
